import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  TextField,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PhoneCallback as PhoneCallbackIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Language as BrowserIcon,
  Label as TagIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import AdminApi from '../../Service/Admin.api';

const CallbackRequestsList: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchCallbackRequests();
  }, [page, pageSize, debouncedSearch]);

  const fetchCallbackRequests = async () => {
    setLoading(true);
    try {
      const res = await AdminApi.getCallbackRequests(page + 1, pageSize, debouncedSearch);
      if (res.success && res.data) {
        setRequests(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching callback requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await AdminApi.exportCallbackRequests();
      if (res.success && res.data) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `CallbackRequests_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      console.error('Error exporting callback requests:', error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getWhatsAppLink = (phone: string) => {
    const cleanNumber = phone.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 10) {
      return `https://wa.me/91${cleanNumber}`;
    }
    return `https://wa.me/${cleanNumber}`;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PhoneCallbackIcon sx={{ fontSize: 36, color: '#283165', mr: 1.5 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1c234b', fontFamily: '"Outfit", sans-serif' }}>
            Callback Requests
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading}
            sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Export to Excel
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchCallbackRequests} disabled={loading} sx={{ bgcolor: 'rgba(40, 49, 101, 0.04)', '&:hover': { bgcolor: 'rgba(40, 49, 101, 0.1)' } }}>
              <RefreshIcon sx={{ color: '#283165' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters & Search */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search by name, phone, city, browser, OS, location, or source URL..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.primary', mr: 1, fontSize: 20 }} />,
                sx: { borderRadius: '10px' },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3, boxShadow: '0 8px 32px rgba(31, 38, 135, 0.04)', border: '1px solid #f1f5f9' }}>
        {loading && requests.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress size={40} sx={{ color: '#283165' }} />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 290px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Contact Details</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Location & City</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Browser & OS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Source URL</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#475569' }}>Date Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.primary">No callback requests found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((row) => (
                      <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                          {row.name}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#1e293b' }}>
                            {row.mobileNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#334155' }}>
                            {row.city || 'N/A'}
                          </Typography>
                          {row.location && (
                            <Typography variant="caption" color="text.primary" display="block">
                              Loc: {row.location}
                            </Typography>
                          )}
                          {row.ipAddress && (
                            <Typography variant="caption" color="text.primary" display="block">
                              IP: {row.ipAddress}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <BrowserIcon sx={{ fontSize: 16, color: '#64748b' }} />
                            <Typography variant="body2" sx={{ color: '#334155' }}>
                              {row.browser || 'Unknown'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.primary" display="block">
                            OS: {row.os || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>
                          {row.sourceUrl ? (
                            <Tooltip title={row.sourceUrl}>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <LinkIcon sx={{ fontSize: 14, color: '#2563eb' }} />
                                <Typography variant="caption" sx={{ color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {row.sourceUrl}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.primary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155' }}>
                          {formatDate(row.dtCreated)}
                        </TableCell>

                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CallbackRequestsList;
